/**
 * The whole library, in a list you can scroll (#1337).
 *
 * ⚠️ THE DECK IS NOT THIS. The deck shows one card at a time on purpose — three
 * swipes fill three days, and a wall of thirty-six choices is how people put
 * the phone down without a plan. But a decision funnel is a bad way to answer
 * "what can this thing even cook", and there was no other way to look.
 *
 * ⛔ THE POINT OF THIS SCREEN IS THE SECOND SECTION. The engine already knows
 * exactly why it withheld every meal it withheld, and until now nobody could
 * see it — so a household with a bean-avoider simply never saw chili and had no
 * way to find out why. Showing the offered meals is the ordinary half; showing
 * the refusals with their reasons is the half that answers a real question.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { twoPlates, type Deck } from "../api/twoPlates";

export default function Cookbook() {
  const [q, setQ] = useState("");
  const deck = useQuery<Deck>({ queryKey: ["deck"], queryFn: () => twoPlates.deck() });

  const needle = q.trim().toLowerCase();
  const offered = useMemo(
    () =>
      (deck.data?.offered ?? []).filter(
        (m) =>
          !needle ||
          m.name.toLowerCase().includes(needle) ||
          m.anchor.toLowerCase().includes(needle) ||
          m.sides.some((s) => s.toLowerCase().includes(needle)),
      ),
    [deck.data, needle],
  );
  const breakfasts = useMemo(
    () =>
      (deck.data?.breakfasts ?? []).filter(
        (m) =>
          !needle ||
          m.name.toLowerCase().includes(needle) ||
          m.anchor.toLowerCase().includes(needle),
      ),
    [deck.data, needle],
  );
  const dropped = useMemo(
    () =>
      (deck.data?.dropped ?? []).filter((m) => !needle || m.name.toLowerCase().includes(needle)),
    [deck.data, needle],
  );

  if (deck.isLoading) return <p className="text-muted p-4 text-sm">Loading…</p>;
  if (deck.isError) return <p className="text-warn p-4 text-sm">Could not load the cookbook.</p>;

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold">Cookbook</h1>
          <p className="text-muted mt-1 max-w-prose text-sm">
            Everything in the library, and everything it is holding back from you.
          </p>
        </div>
        {/**
         * ⛔ ADDING A RECIPE WAS EFFECTIVELY UNREACHABLE. The import screen is
         * built, routed and working — and the ONLY way in was a link on the
         * deck that appears after you have swiped through every meal in the
         * library and run out ("that is every meal you can eat"). The single
         * moment the app offered to take a recipe was the moment you had
         * already given up finding something to cook.
         *
         * It belongs here. This is the screen you open to ask what the library
         * holds, which is exactly when you notice what it is missing.
         */}
        <Link
          to="/import"
          className="bg-plate-b shrink-0 rounded-lg px-3 py-2 text-sm font-semibold text-white"
        >
          Add a recipe
        </Link>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search meals, mains, sides"
        className="border-line mt-4 w-full rounded-lg border px-3 py-2 text-sm"
      />

      <p className="text-muted mt-4 text-xs uppercase tracking-wide">
        {offered.length} dinners you can cook
      </p>

      <div className="mt-2 flex flex-col gap-2">
        {offered.map((m) => (
          <Link
            key={m.mealId}
            to={`/cook/${m.mealId}`}
            className="border-line block rounded-lg border p-3"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-medium">
                {m.starred && <span aria-label="starred">★ </span>}
                {m.name}
              </span>
              {/* ⚠️ Active minutes, never total — "480 minutes" is a lie about a
                  slow cooker. */}
              <span className="text-muted shrink-0 text-xs">{m.activeMinutes} min</span>
            </div>

            <p className="text-muted mt-0.5 text-sm">
              {m.anchor}
              {m.sides.length > 0 && <> · {m.sides.join(", ")}</>}
            </p>

            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {m.slowCooker && <Tag>slow cooker</Tag>}
              {m.makesLunch && <Tag>makes lunch</Tag>}
              {m.timesCooked > 0 && (
                <Tag>
                  cooked {m.timesCooked} time{m.timesCooked === 1 ? "" : "s"}
                </Tag>
              )}
            </div>

            {/* Written after cooking it, shown before cooking it again. */}
            {m.note && <p className="text-muted mt-1.5 text-xs italic">“{m.note}”</p>}
          </Link>
        ))}
      </div>

      {breakfasts.length > 0 && (
        <>
          <p className="text-muted mt-8 text-xs uppercase tracking-wide">
            {breakfasts.length} for breakfast
          </p>
          <div className="mt-2 flex flex-col gap-2">
            {breakfasts.map((m) => (
              <Link
                key={m.mealId}
                to={`/cook/${m.mealId}`}
                className="border-line block rounded-lg border p-3"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium">{m.name}</span>
                  <span className="text-muted shrink-0 text-xs">{m.activeMinutes} min</span>
                </div>
                <p className="text-muted mt-0.5 text-sm">
                  {m.anchor}
                  {m.sides.length > 0 && <> · {m.sides.join(", ")}</>}
                </p>
              </Link>
            ))}
          </div>
        </>
      )}

      {dropped.length > 0 && (
        <>
          <p className="text-muted mt-8 text-xs uppercase tracking-wide">
            {dropped.length} we are not offering
          </p>
          <p className="text-muted mt-1 max-w-prose text-sm">
            Not hidden — held back, for a reason you can read. Change what
            somebody eats and these come back on their own.
          </p>

          <div className="mt-2 flex flex-col gap-2">
            {dropped.map((m) => (
              <div key={m.mealId} className="border-line rounded-lg border border-dashed p-3">
                <span className="font-medium">{m.name}</span>
                <p className="text-muted mt-0.5 text-sm">{m.reason}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="border-line text-muted rounded-full border px-2 py-0.5 text-[11px]">
      {children}
    </span>
  );
}
